import React, { useState, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { disableEngagementPlanErrorSelector, disableEngagementPlanSuccessSelector } from '@redux/tasks/tasksSelectors';
import { disableEngagementPlanThunk } from '@redux/tasks/tasksThunks';
import { EngagementPlan } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import { getActiveOrganizationIdSelector } from '@redux/organization/organizationSelectors';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { ConfirmationDialogV2 } from 'components/ConfirmationDialog/ConfirmationDialogV2';
import { WarningCircleIcon } from 'components/Icons';

export type CustomProps = {
  plan: EngagementPlan | null;
  visible: boolean;
  onClosePress: () => void;
  onSuccess?: (plan: EngagementPlan) => void;
};

const messages = {
  title: 'Delete',
  description: 'You sure you want to delete? This cannot be undone.',
  proceedText: 'Delete plan',
  closeText: 'Keep plan',
  success: 'Plan was deleted successfully!',
  error: 'Something went wrong while deleting the plan. Please try again.'
};

type PropsFromRedux = ConnectedProps<typeof connectRedux>;
type Props = PropsFromRedux & CustomProps;

const DeletePlanModal: React.FC<Props> = ({ plan, visible, onClosePress, onSuccess, disablePlanCallback, disablePlanError, disablePlanSuccess, activeOrganizationId }) => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const { showAlertInfo, showAlertWarning } = useAlert();

  useEffect(() => {
    if (!visible) {
      setIsProcessing(false);
    }
  }, [visible]);

  useEffect(() => {
    if (isProcessing) {
      if (disablePlanSuccess) {
        plan && onSuccess?.(plan);
        showAlertInfo({
          message: messages.success
        });
        setIsProcessing(false);
        onClosePress();
      }

      if (disablePlanError) {
        showAlertWarning({
          message: messages.error
        });
        console.log('Failed to disable plan', disablePlanError);
        setIsProcessing(false);
        onClosePress();
      }
    }
  }, [disablePlanSuccess, disablePlanError, isProcessing]);

  const onConfirm = async () => {
    if (!plan?.id) {
      console.log('Plan ID is not provided');
      return;
    }

    if (!activeOrganizationId) {
      console.log('Organization ID is not provided');
      return;
    }

    setIsProcessing(true);
    disablePlanCallback(plan.id, activeOrganizationId);
  };

  return (
    <ConfirmationDialogV2
      open={visible}
      icon={WarningCircleIcon}
      title={`${messages.title} ${plan?.title || ''}`}
      description={messages.description}
      proceedEvent={onConfirm}
      proceedEventText={messages.proceedText}
      proceedLoading={isProcessing}
      closeEvent={onClosePress}
      closeEventText={messages.closeText}
    />
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    disablePlanError: disableEngagementPlanErrorSelector(state),
    disablePlanSuccess: disableEngagementPlanSuccessSelector(state),
    activeOrganizationId: getActiveOrganizationIdSelector(state)
  }),
  {
    disablePlanCallback: disableEngagementPlanThunk
  }
);

export default compose(connectRedux)(DeletePlanModal);
